/**
 * Ancora del clock CRA Art.14 (thread SRP): mostra l'istante di rilevazione da cui
 * partono le scadenze (24h / 72h / 14gg) e permette all'analista di correggerlo con una
 * motivazione (es. la vulnerabilità era nota prima della promozione del triage).
 *
 * PATCH /sla/timers/:id/detected-at — la correzione è tracciata in audit; le scadenze sono
 * derivate dal timer, quindi lista, dettaglio e colonna prodotti si aggiornano da sole.
 */

import { useState } from 'react';
import { ApiError } from '../../api/client.js';
import { useCorrectSlaDetection } from './hooks.js';
import type { SlaTimerAnchor } from './types.js';
import styles from './Srp.module.css';

/** Formatta un ISO come data/ora locale leggibile. */
function fmtDataOra(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
}

/** ISO → valore per `<input type="datetime-local">` (ora locale, minuti). */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface SlaDetectionPanelProps {
  /** Timer SLA del soggetto; assente → nessun clock correggibile (il pannello non compare). */
  timer: SlaTimerAnchor | null | undefined;
  /** Invocato dopo una correzione riuscita (es. per ricaricare il dettaglio che ne dipende). */
  onCorrected?: () => void;
  /** false → sola lettura (utente senza VULNERABILITY_ANALYSIS). Default true. */
  canEdit?: boolean;
}

export function SlaDetectionPanel({ timer, onCorrected, canEdit = true }: SlaDetectionPanelProps) {
  const [inModifica, setInModifica] = useState(false);
  const [valore, setValore] = useState('');
  const [motivo, setMotivo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const correzione = useCorrectSlaDetection();

  if (!timer) return null;

  function apriModifica() {
    if (!timer) return;
    setValore(toDatetimeLocal(timer.detectedAt));
    setMotivo('');
    setErrore(null);
    setInModifica(true);
  }

  function salva() {
    if (!timer) return;
    const data = new Date(valore);
    if (!valore || Number.isNaN(data.getTime())) {
      setErrore('Indica una data e ora valide.');
      return;
    }
    if (data.getTime() > Date.now()) {
      setErrore('La data di rilevazione non può essere nel futuro.');
      return;
    }
    if (motivo.trim().length === 0) {
      setErrore('La motivazione è obbligatoria.');
      return;
    }
    setErrore(null);
    correzione.mutate(
      { timerId: timer.id, detectedAt: data.toISOString(), reason: motivo.trim() },
      {
        onSuccess: () => {
          setInModifica(false);
          onCorrected?.();
        },
        onError: (err) => {
          setErrore(err instanceof ApiError ? err.message : (err.message ?? 'Errore'));
        },
      },
    );
  }

  return (
    <div className={styles.section ?? ''} data-testid="sla-rilevazione">
      <p className={styles.sectionTitle ?? ''}>Rilevazione (avvio scadenze CRA)</p>
      <p>
        <span data-testid="sla-detected-at">{fmtDataOra(timer.detectedAt)}</span>
        {timer.detectedAtCorrectedBy && (
          <span className={styles.muted ?? ''} data-testid="sla-correzione-info">
            {' '}
            — corretta da {timer.detectedAtCorrectedBy}
            {timer.detectedAtCorrectedAt ? ` il ${fmtDataOra(timer.detectedAtCorrectedAt)}` : ''}
            {timer.detectedAtCorrectionReason ? `: ${timer.detectedAtCorrectionReason}` : ''}
          </span>
        )}
      </p>

      {canEdit && !inModifica && (
        <div className={styles.formActions}>
          <button
            type="button"
            className={`${styles.btnSecondary} ${styles.btnSmall}`}
            data-testid="sla-correggi"
            onClick={apriModifica}
          >
            Correggi data di rilevazione
          </button>
        </div>
      )}

      {canEdit && inModifica && (
        <div className={styles.formCard ?? ''} data-testid="sla-correzione-form">
          <div className={styles.formGroup}>
            <label htmlFor="sla-detected-input" className={styles.formGroupLabel}>
              Rilevata il
            </label>
            <input
              id="sla-detected-input"
              type="datetime-local"
              className={styles.formInput}
              data-testid="sla-detected-input"
              value={valore}
              max={toDatetimeLocal(new Date().toISOString())}
              onChange={(e) => {
                setValore(e.target.value);
              }}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="sla-reason-input" className={styles.formGroupLabel}>
              Motivazione (obbligatoria)
            </label>
            <textarea
              id="sla-reason-input"
              className={styles.formInput}
              data-testid="sla-reason-input"
              rows={2}
              style={{ width: '100%', resize: 'vertical' }}
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value);
              }}
            />
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPrimary}
              data-testid="sla-salva"
              disabled={correzione.isPending}
              onClick={salva}
            >
              {correzione.isPending ? 'Salvataggio...' : 'Salva correzione'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              data-testid="sla-annulla"
              disabled={correzione.isPending}
              onClick={() => {
                setInModifica(false);
                setErrore(null);
              }}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {errore && (
        <span className={styles.inlineMutError} data-testid="sla-errore">
          {errore}
        </span>
      )}
    </div>
  );
}
